import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Team, User } from "@shared/schema";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { X, Users, Building2, User as UserIcon, Search, Info } from "lucide-react";
import { cn } from "@/lib/utils";

type AudienceMode = "everyone" | "teams" | "individuals";

interface TeamWithMembers extends Team {
  memberCount?: number;
}

interface AudienceSelectorProps {
  selectedTeamIds: number[];
  onTeamIdsChange: (teamIds: number[]) => void;
  visibleToAllTeams: boolean;
  onVisibleToAllTeamsChange: (value: boolean) => void;
  selectedUserIds?: number[];
  onUserIdsChange?: (userIds: number[]) => void;
  label?: string;
  required?: boolean;
  showIndividuals?: boolean;
  description?: string;
}

export function AudienceSelector({
  selectedTeamIds,
  onTeamIdsChange,
  visibleToAllTeams,
  onVisibleToAllTeamsChange,
  selectedUserIds = [],
  onUserIdsChange,
  label = "Who should see this?",
  required = false,
  showIndividuals = false,
  description,
}: AudienceSelectorProps) {
  const [teamSearchQuery, setTeamSearchQuery] = useState("");
  const [userSearchQuery, setUserSearchQuery] = useState("");

  // Determine current mode based on state
  const currentMode: AudienceMode = useMemo(() => {
    if (visibleToAllTeams) return "everyone";
    if (selectedUserIds.length > 0 && selectedTeamIds.length === 0) return "individuals";
    return "teams";
  }, [visibleToAllTeams, selectedTeamIds.length, selectedUserIds.length]);

  // Fetch teams with member counts
  const { data: teams = [], isLoading: teamsLoading } = useQuery<TeamWithMembers[]>({
    queryKey: ["/api/teams"],
  });

  // Fetch team members for count calculation
  const { data: teamMembers = [] } = useQuery<any[]>({
    queryKey: ["/api/team-members"],
    enabled: teams.length > 0,
  });

  // Fetch users for individual selection
  const { data: users = [], isLoading: usersLoading } = useQuery<User[]>({
    queryKey: ["/api/users"],
    enabled: showIndividuals,
  });

  // Calculate member counts for each team
  const teamsWithCounts = useMemo(() => {
    return teams.map(team => ({
      ...team,
      memberCount: teamMembers.filter((tm: any) => tm.teamId === team.id).length,
    }));
  }, [teams, teamMembers]);

  // Calculate total reach
  const reachCount = useMemo(() => {
    if (visibleToAllTeams) {
      return users.length || "all employees";
    }
    if (currentMode === "individuals") {
      return selectedUserIds.length;
    }
    // Calculate unique users in selected teams
    const selectedMemberIds = new Set<number>();
    teamMembers
      .filter((tm: any) => selectedTeamIds.includes(tm.teamId))
      .forEach((tm: any) => selectedMemberIds.add(tm.userId));
    return selectedMemberIds.size;
  }, [visibleToAllTeams, currentMode, selectedTeamIds, selectedUserIds, teamMembers, users.length]);

  // Filter teams by search
  const filteredTeams = useMemo(() => {
    if (!teamSearchQuery) return teamsWithCounts;
    return teamsWithCounts.filter(team =>
      team.name.toLowerCase().includes(teamSearchQuery.toLowerCase())
    );
  }, [teamsWithCounts, teamSearchQuery]);

  // Filter users by search
  const filteredUsers = useMemo(() => {
    if (!userSearchQuery) return users;
    return users.filter(user =>
      user.name.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(userSearchQuery.toLowerCase())
    );
  }, [users, userSearchQuery]);

  const handleModeChange = (mode: AudienceMode) => {
    switch (mode) {
      case "everyone":
        onVisibleToAllTeamsChange(true);
        onTeamIdsChange([]);
        onUserIdsChange?.([]);
        break;
      case "teams":
        onVisibleToAllTeamsChange(false);
        onUserIdsChange?.([]);
        break;
      case "individuals":
        onVisibleToAllTeamsChange(false);
        onTeamIdsChange([]);
        break;
    }
  };

  const handleTeamToggle = (teamId: number) => {
    if (selectedTeamIds.includes(teamId)) {
      onTeamIdsChange(selectedTeamIds.filter((id) => id !== teamId));
    } else {
      onTeamIdsChange([...selectedTeamIds, teamId]);
    }
  };

  const handleUserToggle = (userId: number) => {
    if (!onUserIdsChange) return;
    if (selectedUserIds.includes(userId)) {
      onUserIdsChange(selectedUserIds.filter((id) => id !== userId));
    } else {
      onUserIdsChange([...selectedUserIds, userId]);
    }
  };

  const selectedTeams = teamsWithCounts.filter((team) =>
    selectedTeamIds.includes(team.id)
  );

  const selectedUsers = users.filter((user) =>
    selectedUserIds.includes(user.id)
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </Label>
      </div>

      {description && (
        <p className="text-sm text-muted-foreground">{description}</p>
      )}

      {/* Mode Selection */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        <button
          type="button"
          onClick={() => handleModeChange("everyone")}
          className={cn(
            "flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all",
            currentMode === "everyone"
              ? "border-primary bg-primary/5 text-primary"
              : "border-border hover:border-border hover:bg-muted"
          )}
        >
          <Building2 className="h-6 w-6 mb-2" />
          <span className="text-sm font-medium">Everyone</span>
          <span className="text-xs text-muted-foreground mt-1">Entire org</span>
        </button>

        <button
          type="button"
          onClick={() => handleModeChange("teams")}
          className={cn(
            "flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all",
            currentMode === "teams"
              ? "border-primary bg-primary/5 text-primary"
              : "border-border hover:border-border hover:bg-muted"
          )}
        >
          <Users className="h-6 w-6 mb-2" />
          <span className="text-sm font-medium">Specific Teams</span>
          <span className="text-xs text-muted-foreground mt-1">Select teams</span>
        </button>

        {showIndividuals && (
          <button
            type="button"
            onClick={() => handleModeChange("individuals")}
            className={cn(
              "flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all",
              currentMode === "individuals"
                ? "border-primary bg-primary/5 text-primary"
                : "border-border hover:border-border hover:bg-muted"
            )}
          >
            <UserIcon className="h-6 w-6 mb-2" />
            <span className="text-sm font-medium">Individuals</span>
            <span className="text-xs text-muted-foreground mt-1">Pick people</span>
          </button>
        )}
      </div>

      {/* Team Selection */}
      {currentMode === "teams" && (
        <div className="space-y-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search teams..."
              value={teamSearchQuery}
              onChange={(e) => setTeamSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Team List */}
          <div className="border rounded-lg max-h-64 overflow-y-auto">
            {teamsLoading ? (
              <div className="p-4 text-center text-muted-foreground">
                Loading teams...
              </div>
            ) : filteredTeams.length === 0 ? (
              <div className="p-4 text-center text-muted-foreground">
                No teams found
              </div>
            ) : (
              <div className="divide-y">
                {filteredTeams.map((team) => (
                  <label
                    key={team.id}
                    className="flex items-center justify-between p-3 hover:bg-muted cursor-pointer"
                  >
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={selectedTeamIds.includes(team.id)}
                        onCheckedChange={() => handleTeamToggle(team.id)}
                      />
                      <div>
                        <span className="font-medium">{team.name}</span>
                        {team.description && (
                          <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                            {team.description}
                          </p>
                        )}
                      </div>
                    </div>
                    <Badge variant="secondary" className="ml-2">
                      {team.memberCount || 0} {team.memberCount === 1 ? "member" : "members"}
                    </Badge>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Selected Teams */}
          {selectedTeams.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {selectedTeams.map((team) => (
                <Badge
                  key={team.id}
                  variant="outline"
                  className="flex items-center gap-1 bg-primary/5"
                >
                  {team.name}
                  <button
                    type="button"
                    onClick={() => handleTeamToggle(team.id)}
                    className="ml-1 hover:text-destructive"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}

          {/* Validation */}
          {required && selectedTeamIds.length === 0 && (
            <p className="text-sm text-red-500">
              Please select at least one team
            </p>
          )}
        </div>
      )}

      {/* Individual Selection */}
      {currentMode === "individuals" && showIndividuals && (
        <div className="space-y-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search people..."
              value={userSearchQuery}
              onChange={(e) => setUserSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* User List */}
          <div className="border rounded-lg max-h-64 overflow-y-auto">
            {usersLoading ? (
              <div className="p-4 text-center text-muted-foreground">
                Loading people...
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="p-4 text-center text-muted-foreground">
                No people found
              </div>
            ) : (
              <div className="divide-y">
                {filteredUsers.map((user) => (
                  <label
                    key={user.id}
                    className="flex items-center gap-3 p-3 hover:bg-muted cursor-pointer"
                  >
                    <Checkbox
                      checked={selectedUserIds.includes(user.id)}
                      onCheckedChange={() => handleUserToggle(user.id)}
                    />
                    <div className="flex items-center gap-3">
                      {user.photoUrl ? (
                        <img
                          src={user.photoUrl}
                          alt={user.name}
                          className="w-8 h-8 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                          <span className="text-sm font-medium text-muted-foreground">
                            {user.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                      <div>
                        <span className="font-medium">{user.name}</span>
                        <p className="text-xs text-muted-foreground">
                          {user.title || user.department || user.email}
                        </p>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Selected Users */}
          {selectedUsers.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {selectedUsers.map((user) => (
                <Badge
                  key={user.id}
                  variant="outline"
                  className="flex items-center gap-1 bg-primary/5"
                >
                  {user.name}
                  <button
                    type="button"
                    onClick={() => handleUserToggle(user.id)}
                    className="ml-1 hover:text-destructive"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}

          {/* Validation */}
          {required && selectedUserIds.length === 0 && (
            <p className="text-sm text-red-500">
              Please select at least one person
            </p>
          )}
        </div>
      )}

      {/* Reach Preview */}
      <div className="flex items-center gap-2 p-3 bg-muted rounded-lg border">
        <Info className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm">
          <span className="font-medium">Reach:</span>{" "}
          {typeof reachCount === "number" ? (
            <>
              <span className="text-primary font-semibold">~{reachCount}</span>{" "}
              {reachCount === 1 ? "person" : "people"}
            </>
          ) : (
            <span className="text-primary font-semibold">{reachCount}</span>
          )}
        </span>
      </div>
    </div>
  );
}
